import { combineReducers } from 'redux';
import { pathOr, uniq, values, propOr, indexBy, prop, omit } from 'ramda';
import { createSelector } from 'reselect';
import memoize from 'lodash.memoize';

import { actions } from '../actions/boardgames';

import { categoriesSubsetSelector } from './categories';

const byId = (state = {}, action) => {
  switch (action.type) {
    case actions.FETCH_VISIBLE_BOARDGAMES_SUCCESS:
    case actions.FETCH_BOARDGAMES_SUCCESS:
    case actions.CREATE_BOARDGAME_SUCCESS:
    case actions.GET_BOARDGAME_SUCCESS:
    case actions.MARK_BOARDGAME_AS_SELECTION_SUCCESS:
    case actions.SET_BOARDGAME_VISIBILITY_SUCCESS:
      return Object.assign({}, state, omit(['undefined'], pathOr({}, ['payload', 'entities', 'boardgames'], action)));
    default:
      return state;
  }
};

const visibleIds = (state = [], action) => {
  switch (action.type) {
    case actions.FETCH_VISIBLE_BOARDGAMES_SUCCESS:
      return pathOr([], ['payload', 'result'], action);
    default:
      return state;
  }
};

const allIds = (state = [], action) => {
  switch (action.type) {
    case actions.FETCH_VISIBLE_BOARDGAMES_SUCCESS:
    case actions.FETCH_BOARDGAMES_SUCCESS:
      return uniq([
        ...state,
        ...pathOr([], ['payload', 'result'], action),
      ]);
    case actions.CREATE_BOARDGAME_SUCCESS:
        return action.payload.result
          ? [ ...state, action.payload.result ]
          : [ ...state, action.payload ];

    default:
      return state;
  }
};

const loading = (state = false, action) => {
  switch (action.type) {
    case actions.FETCH_BOARDGAMES_REQUEST:
    case actions.FETCH_VISIBLE_BOARDGAMES_REQUEST:
    case actions.RESERVE_BOARDGAME_REQUEST:
    case actions.CREATE_BOARDGAME_REQUEST:
    case actions.GET_BACK_BOARDGAME_REQUEST:
    case actions.MARK_BOARDGAME_AS_SELECTION_REQUEST:
    case actions.SET_BOARDGAME_VISIBILITY_REQUEST:
    case actions.GET_BOARDGAME_REQUEST:
      return true;
    case actions.FETCH_VISIBLE_BOARDGAMES_SUCCESS:
    case actions.FETCH_BOARDGAMES_SUCCESS:
    case actions.RESERVE_BOARDGAME_SUCCESS:
    case actions.FETCH_BOARDGAMES_FAILED:
    case actions.FETCH_VISIBLE_BOARDGAMES_FAILED:
    case actions.RESERVE_BOARDGAME_FAILED:
    case actions.CREATE_BOARDGAME_SUCCESS:
    case actions.CREATE_BOARDGAME_FAILED:
    case actions.GET_BOARDGAME_SUCCESS:
    case actions.GET_BOARDGAME_FAILED:
    case actions.MARK_BOARDGAME_AS_SELECTION_SUCCESS:
    case actions.MARK_BOARDGAME_AS_SELECTION_FAILED:
    case actions.SET_BOARDGAME_VISIBILITY_SUCCESS:
    case actions.SET_BOARDGAME_VISIBILITY_FAILED:
      return false;
    default:
      return state;
  }
};

const boardgames = combineReducers({
  byId,
  visibleIds,
  allIds,
  loading,
});

export default boardgames;

const visibleBoardgameIdsSelector = state => state.boardgames.visibleIds;
const allBoardgameIdsSelector = state => state.boardgames.allIds;
const boardgamesSelector = state => state.boardgames.byId;

export const visibleBoardgamesSelector = createSelector(
  visibleBoardgameIdsSelector,
  boardgamesSelector,
  categoriesSubsetSelector,
  (ids, boardgames, categoriesSelector) => ids
    .map(id => Object.assign({}, boardgames[id], {
      categories: categoriesSelector(propOr([], 'categories', boardgames[id])),
    }))
);

export const visibleReservedBoardgamesSelector = createSelector(
  visibleBoardgameIdsSelector,
  boardgamesSelector,
  categoriesSubsetSelector,
  (ids, boardgames, categoriesSelector) => ids
    .map(id => Object.assign({}, boardgames[id], {
      categories: categoriesSelector(propOr([], 'categories', boardgames[id])),
    }))
    .filter(bg => bg.reservedCopies && bg.reservedCopies > 0)
);

export const visibleBoardgamesWithoutReservationSelector = createSelector(
  visibleBoardgameIdsSelector,
  boardgamesSelector,
  categoriesSubsetSelector,
  (ids, boardgames, categoriesSelector) => ids
    .map(id => Object.assign({}, boardgames[id], {
      categories: categoriesSelector(propOr([], 'categories', boardgames[id])),
    }))
    .filter(bg => !bg.reservedCopies || bg.reservedCopies === 0)
);

export const visibleBoardgamesByIdSelector = createSelector(
  visibleBoardgameIdsSelector,
  boardgamesSelector,
  categoriesSubsetSelector,
  (ids, boardgames, categoriesSelector) => indexBy(prop('id'), ids
    .map(id => Object.assign({}, boardgames[id], {
      categories: categoriesSelector(propOr([], 'categories', boardgames[id])),
    })))
)

export const starredBoardgamesSelector = createSelector(
  visibleBoardgameIdsSelector,
  boardgamesSelector,
  (ids, boardgames) => ids
    .map(id => Object.assign({}, boardgames[id]))
    .filter(bg => bg.selection)
);

export const allBoardgamesSelector = createSelector(
  boardgamesSelector,
  allBoardgameIdsSelector,
  (boardgames, ids) => ids.map(id => boardgames[id])
);

export const maxNumberOfPlayersSelector = createSelector(
  boardgamesSelector,
  boardgames => Math.max(...(values(boardgames)).map(bg => bg.maxPlayers || bg.minPlayers))
);

export const boardgameSelector = createSelector(
  boardgamesSelector,
  boardgames => memoize(
    id => id && boardgames[id]
  )
);

export const fullBoardgameSelector = createSelector(
  boardgamesSelector,
  categoriesSubsetSelector,
  (boardgames, categoriesSelector) => memoize(
    id => id && boardgames[id] && Object.assign({}, boardgames[id], {
      categories: categoriesSelector(propOr([], 'categories', boardgames[id])),
    })
  )
);

export const loadingSelector = state => state.boardgames.loading;

export const getBackAllBoardgamesButtonEnabledSelector = createSelector(
  visibleBoardgamesSelector,
  boardgames => boardgames
    .map(bg => bg.reservedCopies > 0)
    .reduce((enabled, isReserved) => enabled || isReserved, false)
);
